import { useState, useCallback } from "react";

/**
 * Pestanas del panel de administracion: lista, ids ARIA y estado persistido.
 *
 * Vive en un modulo aparte del componente (`Pestanas.tsx`) porque la regla
 * eslint `react-refresh/only-export-components` exige que un fichero de
 * componente no exporte nada mas — mismo criterio que `lib/approvals/money.ts`.
 *
 * Sin ruta por pestana a proposito: nadie comparte el enlace a una pestana del
 * panel, y una ruta anadiria trabajo de router para nada. La pestana activa se
 * recuerda en sessionStorage para que guardar algo no devuelva al admin a
 * Permisos obligandole a buscar otra vez donde estaba.
 */
export const PESTANAS = ["permissions", "apiKeys", "widgets", "modelos"] as const;

export type PestanaAdmin = (typeof PESTANAS)[number];

export const TAB_STORAGE_KEY = "admin_tab";

const POR_DEFECTO: PestanaAdmin = "permissions";

function esPestana(valor: unknown): valor is PestanaAdmin {
  return PESTANAS.includes(valor as PestanaAdmin);
}

/** Id del boton de una pestana; el panel lo referencia con aria-labelledby. */
export const idPestana = (p: PestanaAdmin) => `admin-tab-${p}`;

/** Id del panel de una pestana; el boton lo referencia con aria-controls. */
export const idPanel = (p: PestanaAdmin) => `admin-panel-${p}`;

/**
 * Estado de la pestana activa, persistido en sessionStorage.
 *
 * El valor leido se VALIDA contra la lista: un valor escrito por una version
 * anterior del panel (o a mano en devtools) no debe dejar la pagina sin pintar
 * ninguna seccion. Y toda la I/O va en try/catch — sessionStorage puede lanzar
 * en modo privado o con el almacenamiento bloqueado, y eso no puede tumbar el
 * panel entero (mismo patron que lib/auth/redirectFallback).
 */
export function usePestanaAdmin(): [PestanaAdmin, (p: PestanaAdmin) => void] {
  const [pestana, setPestanaState] = useState<PestanaAdmin>(() => {
    try {
      const guardada = sessionStorage.getItem(TAB_STORAGE_KEY);
      return esPestana(guardada) ? guardada : POR_DEFECTO;
    } catch {
      return POR_DEFECTO;
    }
  });

  const setPestana = useCallback((p: PestanaAdmin) => {
    setPestanaState(p);
    try {
      sessionStorage.setItem(TAB_STORAGE_KEY, p);
    } catch {
      /* sessionStorage puede fallar en modo privado; no es critico */
    }
  }, []);

  return [pestana, setPestana];
}
